
import React, { useState } from 'react';
import type { AdminUser, Department, DayOfWeek, AvailabilityStatus } from '../../types';
import { Icons } from '../ui/Icons';
import AvailabilityCalendar from '../staff/AvailabilityCalendar';

interface AdminProfileViewProps {
    adminUser: AdminUser;
    departments: Department[];
    weekStartDay: DayOfWeek;
    onSave: (admin: AdminUser) => void;
}

const AdminProfileView: React.FC<AdminProfileViewProps> = ({ adminUser, departments, weekStartDay, onSave }) => {
    const [activeTab, setActiveTab] = useState<'profile' | 'availability' | 'settings'>('profile');
    const [formData, setFormData] = useState<AdminUser>(adminUser);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        if (type === 'checkbox') {
            const { checked } = e.target as HTMLInputElement;
            setFormData({ ...formData, [name]: checked });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleOverridesChange = (overrides: { [date: string]: AvailabilityStatus; }) => {
        setFormData(prev => ({ ...prev, availabilityOverrides: overrides }));
    };

    const handleDefaultAvailabilityChange = (day: DayOfWeek, status: AvailabilityStatus) => {
        setFormData(prev => ({
            ...prev,
            availability: { ...prev.availability, [day]: status }
        }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(formData);
    };

    const renderProfileTab = () => (
        <div className="space-y-4">
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Full Name</label>
                    <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} required className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-200" />
                </div>
                <div>
                    <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Role</label>
                    <input type="text" name="role" id="role" value={formData.role} onChange={handleChange} required className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-200" />
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email (Cannot be changed)</label>
                    <input type="email" name="email" id="email" value={formData.email} disabled className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 bg-gray-100 dark:bg-slate-600 dark:text-gray-400 sm:text-sm" />
                </div>
                <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Phone Number</label>
                    <input type="tel" name="phone" id="phone" value={formData.phone} onChange={handleChange} required className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-200" />
                </div>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Departments (First is Primary)</label>
                <div className="space-y-2">
                    {departments.map(dep => (
                        <label key={dep.id} className="flex items-center gap-2 p-2 rounded-md hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer border border-transparent hover:border-gray-200 dark:hover:border-slate-700 transition-all">
                            <input
                                type="checkbox"
                                checked={formData.departmentIds?.includes(dep.id) || false}
                                onChange={(e) => {
                                    const currentIds = formData.departmentIds || [];
                                    const newIds = e.target.checked
                                        ? [...currentIds, dep.id]
                                        : currentIds.filter(id => id !== dep.id);
                                    setFormData(prev => ({ ...prev, departmentIds: newIds }));
                                }}
                                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">{dep.name}</span>
                            {formData.departmentIds?.[0] === dep.id && (
                                <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-1.5 py-0.5 rounded">Primary</span>
                            )}
                        </label>
                    ))}
                </div>
            </div>
            <div className="flex items-center gap-2 py-2">
                <input
                    type="checkbox"
                    id="isDriver"
                    name="isDriver"
                    checked={formData.isDriver || false}
                    onChange={handleChange}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="isDriver" className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <Icons.Bus className="w-4 h-4 text-indigo-500" />
                    I am a Driver
                </label>
            </div>
            <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
                <textarea name="notes" id="notes" value={formData.notes} onChange={handleChange} rows={3} className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-200"></textarea>
            </div>
        </div>
    );

    const renderAvailabilityTab = () => (
        // FIX: The AvailabilityCalendar component was called without the required 'variant' prop. It's now split into two calls, one for 'overrides' and one for 'defaults', to provide full editing capability within the tab.
        <div>
            <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-2">Date-Specific Overrides</h4>
            <AvailabilityCalendar
                availability={formData.availability}
                availabilityOverrides={formData.availabilityOverrides || {}}
                weekStartDay={weekStartDay}
                onOverridesChange={handleOverridesChange}
                onAvailabilityChange={handleDefaultAvailabilityChange}
                variant="overrides"
            />
            <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mt-6 mb-2">Default Weekly Schedule</h4>
            <AvailabilityCalendar
                availability={formData.availability}
                availabilityOverrides={formData.availabilityOverrides || {}}
                weekStartDay={weekStartDay}
                onOverridesChange={handleOverridesChange}
                onAvailabilityChange={handleDefaultAvailabilityChange}
                variant="defaults"
            />
        </div>
    );
    
    const renderSettingsTab = () => (
        <div>
            <div className="flex items-start">
                <div className="flex items-center h-5">
                    <input id="showInScheduler" name="showInScheduler" type="checkbox" checked={formData.showInScheduler} onChange={handleChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 dark:border-slate-500 rounded bg-gray-100 dark:bg-slate-600" />
                </div>
                <div className="ml-3 text-sm">
                    <label htmlFor="showInScheduler" className="font-medium text-gray-700 dark:text-gray-300">Show in Scheduler</label>
                    <p className="text-gray-500 dark:text-gray-400">Enable this option to appear on the main schedule grid and be assigned shifts.</p>
                </div>
            </div>
             <div className="mt-4">
                <label htmlFor="color" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Display Color</label>
                <input type="color" name="color" id="color" value={formData.color} onChange={handleChange} className="mt-1 block w-full h-10 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm" />
            </div>
        </div>
    );

    const tabs = [
        { id: 'profile', label: 'Profile', icon: <Icons.User className="w-5 h-5"/> },
        { id: 'availability', label: 'Availability', icon: <Icons.CalendarCheck className="w-5 h-5"/> },
        { id: 'settings', label: 'Scheduling Settings', icon: <Icons.Settings className="w-5 h-5"/> },
    ];

    return (
        <div className="max-w-4xl mx-auto">
             <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-6">My Profile</h2>
             <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-800 rounded-lg shadow-md">
                <div className="p-6">
                    <div className="border-b border-gray-200 dark:border-slate-700 mb-6">
                        <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                            {tabs.map(tab => (
                                <button
                                    type="button" key={tab.id} onClick={() => setActiveTab(tab.id as any)}
                                    className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                                        activeTab === tab.id
                                            ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-slate-600'
                                    }`}>
                                    {tab.icon} {tab.label}
                                </button>
                            ))}
                        </nav>
                    </div>
                    {activeTab === 'profile' && renderProfileTab()}
                    {activeTab === 'availability' && renderAvailabilityTab()}
                    {activeTab === 'settings' && renderSettingsTab()}
                </div>
                <div className="bg-gray-50 dark:bg-slate-900/50 px-6 py-4 flex justify-end border-t dark:border-slate-700">
                    <button type="submit" className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-6 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 focus:ring-indigo-500 sm:text-sm">
                        Save Changes
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AdminProfileView;